import Link from "next/link"
import { useRouter } from "next/router"
import { useParams } from "@blitzjs/next"
import { proxy, useProxy } from "valtio"

export const stepperState = proxy({ disabled: false })

export type StepperProps = {
  steps: Array<{
    label: string
    href: (params: Record<any, any>) => string
  }>
}

type StepProps = { index: number; href: string; label: string }

const CompletedStep = ({ href, label }: StepProps) => {
  return (
    <Link href={href} className="group flex items-center w-full">
      <span className="px-6 py-4 flex items-center text-sm font-medium">
        <span className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-secondary-600 rounded-full group-hover:bg-secondary-800">
          {/* Heroicon name: check */}
          <svg
            className="w-6 h-6 text-white"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </span>
        <span className="ml-4 text-sm font-medium text-gray-900">{label}</span>
      </span>
    </Link>
  )
}
const CurrentStep = ({ index, label }: StepProps) => {
  return (
    <div className="px-6 py-4 flex items-center text-sm font-medium">
      <span
        className="flex-shrink-0 w-10 h-10 flex items-center justify-center border-2 border-indigo-600 rounded-full"
        aria-current="step"
      >
        <span className="text-indigo-600">{index + 1}</span>
      </span>
      <span className="ml-4 text-sm font-medium text-indigo-600">{label}</span>
    </div>
  )
}

const FutureStep = ({ index, label }: StepProps) => {
  return (
    <div className="group flex items-center">
      <span className="px-6 py-4 flex items-center text-sm font-medium">
        <span className="flex-shrink-0 w-10 h-10 flex items-center justify-center border-2 border-gray-300 rounded-full ">
          <span className="text-gray-500 ">{index + 1}</span>
        </span>
        <span className="ml-4 text-sm font-medium text-gray-500 ">{label}</span>
      </span>
    </div>
  )
}

export const Stepper = ({ steps }: StepperProps) => {
  const params = useParams()
  const router = useRouter()
  const currentStepIndex = steps.findIndex(
    (step) => step.href(params) === router.asPath.split("?")[0]
  )
  const snapshot = useProxy(stepperState)

  return (
    <nav aria-label="Progress" className="relative">
      <ol className="border border-gray-300 rounded-md divide-y divide-gray-300 md:flex md:divide-y-0">
        {steps.map((step, index) => {
          const href = step.href(params)
          return (
            <li className="relative md:flex-1 md:flex" key={step.label}>
              {index === currentStepIndex ? (
                <CurrentStep label={step.label} href={href} index={index} />
              ) : index > currentStepIndex ? (
                <FutureStep label={step.label} href={href} index={index} />
              ) : (
                <CompletedStep label={step.label} href={href} index={index} />
              )}

              {index < steps.length - 1 && (
                <div
                  className="hidden md:block absolute top-0 right-0 h-full w-5"
                  aria-hidden="true"
                >
                  <svg
                    className="h-full w-full text-gray-300"
                    viewBox="0 0 22 80"
                    fill="none"
                    preserveAspectRatio="none"
                  >
                    <path
                      d="M0 -2L20 40L0 82"
                      vectorEffect="non-scaling-stroke"
                      stroke="currentcolor"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              )}
            </li>
          )
        })}
      </ol>

      {snapshot.disabled && <div className="absolute top-0 bottom-0 right-0 left-0" />}
    </nav>
  )
}
